import { URL_ASSOCIATED_RESOURCE_ENTRY, URL_ASSOCIATED_RESOURCE_QUERY, URL_ASSOCIATED_RESOURCE_UPSERT } from '../constants/urls'
import Route from 'route-parser'
import { of } from 'rxjs'
import { first, switchMap, tap, startWith, map } from 'rxjs/operators'
import observeRequest from 'helpers/fcObserveRequest'
import { toCAPreferences, toComments } from 'requests/associatedresource/transforms'
import { mapResponse, isValidResponse } from 'front-end-core/helpers/dataflow'
import { RESPONSE_PENDING, RESPONSE_BADDATA } from 'front-end-core/constants/responses'
import debug from 'front-end-core/core/debug'
import { toQueryString } from 'front-end-core/core/url'
import { UserUrn } from 'services/SessionService'

const entryRoute = (props) => new Route(URL_ASSOCIATED_RESOURCE_ENTRY).reverse(props)
const upsertRoute = (props) => new Route(URL_ASSOCIATED_RESOURCE_UPSERT).reverse(props)
const queryRoute = (props) => new Route(URL_ASSOCIATED_RESOURCE_QUERY).reverse(props)

export const QUALIFIER_CA_PREFERENCES = 'current-awareness-preferences'
export const QUALIFIER_COMMENTS = 'qa-annotation'

const queryMap = (qualifier) => (resp) => {
  switch (qualifier) {
    case QUALIFIER_CA_PREFERENCES:
      return toCAPreferences(resp)
    case QUALIFIER_COMMENTS:
      return toComments(resp)
    default:
      throw new Error(`AssociatedResource: Qualifier map not found for ${qualifier}`)
  }
}

const withHeaders = (fn) => function () {
  return UserUrn.pipe(
    first(),
    switchMap(urn => fn({ 'X-ESV-IDENTITY': urn }, ...arguments))
  )
}

const upsertWithHeaders = function (headers, qualifier, associatedResourceContentPayload) {
  const logMessage = `Upsert ${qualifier}`

  debug.time(logMessage)
  return observeRequest({
    url: upsertRoute({}),
    headers: { 'Content-Type': 'application/json', ...headers },
    method: 'put',
    body: JSON.stringify({
      qualifier,
      associatedResourceContentPayload
    })
  }).pipe(
    tap(() => debug.timeEnd(logMessage)),
    map(response => {
      response = JSON.parse(response)
      if (!response.status) {
        throw new Error('Upsert failed: ' + response.message)
      }
      return true
    })
  )
}

const queryWithHeaders = function (headers, qualifier, urn, params = { _: Date.now() }) {
  if (!qualifier || !urn) {
    debug.warn(`Can't perform query for ${qualifier} & ${urn}`)
    return of(RESPONSE_BADDATA)
  }

  const logMessage = `Query ${qualifier} ${urn}`

  debug.time(logMessage)
  return observeRequest({
    url: queryRoute({ qualifier, urn }) + toQueryString(params),
    headers,
    method: 'get'
  }).pipe(
    startWith(RESPONSE_PENDING),
    tap(resp => isValidResponse(resp) && debug.timeEnd(logMessage)),
    map(mapResponse(queryMap(qualifier)))
  )
}

const entryWithHeaders = function (headers, urn, params = {}) {
  const logMessage = `Entry ${urn}`

  debug.time(logMessage)
  return observeRequest({
    url: entryRoute({ urn }) + toQueryString(params),
    headers,
    method: 'get'
  }).pipe(
    startWith(RESPONSE_PENDING),
    tap(resp => isValidResponse(resp) && debug.timeEnd(logMessage)),
    map(mapResponse((e) => JSON.parse(JSON.parse(e).content.content)))
  )
}

export const entry = withHeaders(entryWithHeaders)
export const query = withHeaders(queryWithHeaders)
export const upsert = withHeaders(upsertWithHeaders)
